import React from 'react';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { clearStorage, Navigation, Page } from 'interactive';
import { deepMap, hydrate, Element, Content, no_paragraph_tags, void_tags, text_tags } from './DataStructures';
import { BucketComponent, Sortable } from './DragDrop';
import { uniqueId } from './util';
import InteractiveRenderer from './InteractiveRenderer';
import ComponentToolbox from './ComponentToolbox';
import ErrorBoundary from './ErrorBoundary';
import RichText from './controls/RichText';
import CopyComponentButton from './controls/CopyComponentButton';
import { pathReduce, pathAssign, summarizeItem } from './util';
import DestructiveButton from './controls/DestructiveButton';
import './App.css';

// Set to true to debug this file
const DEBUG = false;

// The interactive editor in the center of the app. Can render a page of components, or a text editor with components in it.
export default class InteractiveEditor extends React.Component {
  constructor(props) {
    super(props);
    console.log("InteractiveEditor rootPath", props.rootPathName, props.rootPath);
    this.state = {
      scopedDataParent: props.data,
      scopedDataPath: props.rootPath,
      breadCrumb: [{
        name: props.rootPathName,
        parent: () => props.data,
        path: props.rootPath
      }],
      breadCrumbLength: 1,
      selectedChild: undefined,
      selectedChildren: [],
      changesWereMade: false,
      textMode: false,
      copiedComponent: null
    };
    let children = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
    if (!children) {
      children = [];
    }
    if (children.children) {
      children = children.children;
    }
    this.setState({
      panelLastIndex: children.length
    });
    this.props.onDropLastIndexChanged(children.length);
    this.editorRef = /*#__PURE__*/React.createRef();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.openInspector = this.openInspector.bind(this);
    this.closeInspector = this.closeInspector.bind(this);
    this.scopeToItem = this.scopeToItem.bind(this);
    this.triggerStateUpdate = this.triggerStateUpdate.bind(this);
    this.onChanged = this.onChanged.bind(this);
    this.setCopiedComponent = this.setCopiedComponent.bind(this);
  }
  setCopiedComponent(newComponent) {
    this.setState({
      copiedComponent: newComponent
    });
  }
  // Opens the data modal for editing a component. Updates the breadcrumb as well.
  openInspector(parent, path, noPush) {
    var _path;
    if (!((_path = path) !== null && _path !== void 0 && _path.map)) {
      path = [path];
    }
    let item = parent ? pathReduce(parent, path) : undefined;
    DEBUG && console.log("breadcrumb openInspector called", parent, path, item);
    if (!noPush) {
      this.breadcrumbPush(parent, path, this.openInspector, true);
    }
    if (parent === undefined) {
      // The breadcrumb is already added
      let prev = this.state.breadCrumb[this.state.breadCrumbLength - 1];
      parent = prev.parent();
      DEBUG && console.log("breadcrumb openInspector setState parent was undefined, is now", parent);
    }
    this.props.openInspector(parent, path);
    DEBUG && console.log("openInspectorSuccess");
  }
  // Closes the data modal for editing a component. Restores the breadcrumb to back how it was before.
  closeInspector() {
    // Restore breadcrumb to previous state.
    // We artificially shortened the breadcrumb so that if an edit button
    // is clicked, the new breadcrumb is appended at the right location.
    DEBUG && console.log("breadcrumb closeInspector called");
    this.setState((state, props) => {
      DEBUG && console.log("breadcrumb closeInspector");
      let nonModalIndex = state.breadCrumbLength - 1;
      while (state.breadCrumb[nonModalIndex].fun === this.openInspector) {
        nonModalIndex--;
      }
      props.clearInspector();
      return {
        breadCrumb: state.breadCrumb,
        breadCrumbLength: nonModalIndex + 1
      };
    });
    this.triggerStateUpdate();
  }
  // Pushes a path to the bread crumb. Accepts a callback function for when it's clicked.
  breadcrumbPush(parent, path, fun, artificial) {
    if (!path.map) {
      path = [path];
    }
    let err = new Error();

    // Slice breadCrumb at breadCrumbLength and push to it.
    DEBUG && console.log("breadcrumbPush called", parent, path);
    this.setState(state => {
      let prev = state.breadCrumb[state.breadCrumbLength - 1];
      let previtem = pathReduce(prev.parent(), prev.path);
      if (parent === undefined) {
        parent = previtem;
      }
      let item = pathReduce(parent, path);
      DEBUG && console.log("breadcrumbPush setState", parent, path, item);
      if (previtem === item) {
        DEBUG && console.log("breadcrumbPush: tried to path to same item twice");
        return;
      }
      if (previtem !== parent) {
        err.message = `breadcrumbPush: breadcrumb path discontinuity (parent did not match) (${state.breadCrumb.slice(0, state.breadCrumbLength).map(x => x.path.join(".")).join(">")} => ${summarizeItem(previtem, 1)} !== ${summarizeItem(parent, 1)} -> ${path.join(".")} => ${summarizeItem(item, 1)})`;
        throw err;
      }
      let bc = state.breadCrumb.slice(0, state.breadCrumbLength);
      let name = path.join(".");
      if (item.type) {
        name = item.type + " " + (item.tagName || "");
      }
      bc.push({
        name: name,
        parent: () => pathReduce(this.props.data, bc.slice(0, -1).map(x => x.path).flat()),
        path: path,
        fun: fun,
        onClick: i => {
          this.breadcrumbChop(i + 1);
          fun(undefined, path, artificial);
        }
      });
      DEBUG && console.log("breadCrumbPush new path:", bc.map(x => x.path.join(".")).join(">"));
      return {
        breadCrumb: bc,
        breadCrumbLength: bc.length
      };
    });
  }
  // Shortens the breadcrumb. 
  breadcrumbChop(len, artificial) {
    DEBUG && console.log("breadcrumbChop called");
    this.setState(state => {
      DEBUG && console.log("breadcrumbChop setState", len, artificial);
      if (artificial) {
        DEBUG && console.log("breadCrumb path (artificial):", state.breadCrumb.map(x => x.path.join(".")).slice(0, len).join(">"));
        return {
          breadCrumbLength: len
        };
      }
      let bc = state.breadCrumb.slice(0, len);
      DEBUG && console.log("breadCrumb path:", bc.map(x => x.path.join(".")).join(">"));
      return {
        breadCrumb: bc,
        breadCrumbLength: len
      };
    });
  }
  // Scope the editor into an item, adding to the breadcrumb.
  scopeToItem(parent, path) {
    if (!path.map) {
      path = [path];
    }
    DEBUG && console.log("breadcrumb scopeToItem called", parent, path);
    if (parent !== undefined) {
      this.breadcrumbPush(parent, path, this.scopeToItem);
    }
    this.setState((state, props) => {
      if (parent === undefined) {
        // we already pushed parent to the breadcrumb
        //let prev = state.breadCrumb[state.breadCrumb.length - 1]
        //parent = pathReduce(prev.parent(), prev.path)
        parent = state.breadCrumb[state.breadCrumb.length - 1].parent();
        DEBUG && console.log("breadcrumb scopeToItem setState parent undefined");
      }
      DEBUG && console.log("breadcrumb scopeToItem setState");
      props.clearInspector();
      return {
        scopedDataParent: parent,
        scopedDataPath: path
      };
    });
  }
  // In case of data reserialization and broken references, find the scoped data again using the path and whatever page we're on.
  resetScopedDataReference() {
    DEBUG && console.log("resetScopedDataReference called");
    this.setState((state, props) => {
      let bc = state.breadCrumb.slice();
      bc[0] = {
        name: this.props.rootPathName,
        parent: () => this.props.data,
        path: this.props.rootPath
      };
      let nonModalIndex = state.breadCrumbLength - 1;
      while (bc[nonModalIndex].fun === this.openInspector) {
        nonModalIndex--;
      }
      let last = bc[state.breadCrumbLength - 1];
      let nonModal = bc[nonModalIndex];
      DEBUG && console.log("resetScopedDataReference", nonModal, last, "parent", nonModal.parent(), "path", nonModal.path);
      if (props.inspectorRoot) {
        props.openInspector(last.parent(), last.path);
        return {
          breadCrumb: bc,
          scopedDataParent: nonModal.parent(),
          scopedDataPath: nonModal.path
        };
      }
      return {
        breadCrumb: bc,
        scopedDataParent: nonModal.parent(),
        scopedDataPath: nonModal.path
      };
    });
  }
  // Add a child component.
  onComponentListDrop(index, key, didDrop, hydrated) {
    let children = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
    if (didDrop) {
      children.splice(index, 1, hydrated);
    } else {
      children.splice(index, 1);
    }
    this.onChanged();
  }
  componentDidMount() {
    document.addEventListener("mousedown", this.onDocumentClick);
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocumentClick);
  }
  // Exit text editing mode when clicking away from the editor.
  onDocumentClick(e) {
    this.setState({
      selectedChild: undefined,
      selectedChildren: [],
      textMode: false,
      selectedParent: undefined,
      selectedKey: undefined
    });
  }
  // Keep parent app informed of our state.
  componentDidUpdate(prevprops, prevstate) {
    if (prevprops.rootPath.join(".") !== this.props.rootPath.join(".")) {
      let bc = this.state.breadCrumb.slice();
      bc[0] = {
        name: this.props.rootPathName,
        parent: () => this.props.data,
        path: this.props.rootPath
      };
      this.setState({
        breadCrumb: bc
      });
    }
    if (prevprops.data !== this.props.data && this.state.changesWereMade) {
      this.setState({
        changesWereMade: false
      });
    }
    let children = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
    if (!children) {
      children = [];
    }
    if (children.children) {
      children = children.children;
    }
    if (children.length !== prevstate.panelLastIndex) {
      this.setState({
        panelLastIndex: children.length
      });
      this.props.onDropLastIndexChanged(children.length);
    }
    if (prevstate.breadCrumb !== this.state.breadCrumb || prevstate.breadCrumbLength !== this.state.breadCrumbLength) {
      DEBUG && console.log("breadcrumb changed", this.state.breadCrumbLength, this.state.breadCrumb.map(x => x.path.join(".")).join(">"));
    }
  }
  // Change pages and build a new breadcrumb.
  changePage(i) {
    this.setState((state, props) => {
      DEBUG && console.log("InteractiveEditor changePage", i, props.data === state.breadCrumb[0].parent());
      props.clearInspector();
      return {
        scopedDataParent: props.data,
        scopedDataPath: ["panels", i, "children"],
        breadCrumb: [{
          name: "Page " + (i + 1),
          parent: () => this.props.data,
          path: ["panels", i, "children"]
        }],
        breadCrumbLength: 1,
        textMode: false
      };
    });
  }
  onChanged() {
    this.props.onChanged();
  }
  // Open the data modal, but for the Page component instead of a child component.
  openPageConfig(i) {
    this.openInspector(this.props.data.panels, i);
  }
  // Ensures that everything rerenders and stays synced up. It's inefficient to keep rerendering things, so TODO: revisit this.
  triggerStateUpdate() {
    DEBUG && console.log("textMode triggerStateUpdate");
    this.props.onUpdate();
    if (this.state.changesWereMade === false) {
      this.setState({
        changesWereMade: true
      });
    }
    //this.forceUpdate()
  }
  // Get an empty index at the end of the current list of children.
  getScopedDataLastIndex() {
    let children = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
    DEBUG && console.log("InteractiveEditor getScopedDataLastIndex", children, children.length);
    return children.length;
  }

  // Renders the elements of the interactive with wrappers inserted.
  renderEditorComponents(parent, path) {
    if (parent === undefined || path === undefined) {
      throw new Error("renderEditorComponents: path not valid");
    }
    if (!path.map) {
      path = [path];
    }
    ++this.renderChildren_key;
    DEBUG && console.log("breadcrumb renderEditorComponents pathReduce", parent, path);
    let children = pathReduce(parent, path);
    if (!children) {
      children = [];
    } else if (!children.map) {
      children = [children];
    }
    if (this.state.textMode) {
      DEBUG && console.log("textMode render", parent, path, children);
      return /*#__PURE__*/React.createElement(RichText, {
        value: children,
        onClickNonEditable: () => {
          DEBUG && console.log("InteractiveEditor onClickNonEditable");
          this.setState({
            textMode: false
          });
        },
        onChange: v => {
          DEBUG && console.log("textMode onChange", children);
          pathAssign(parent, path, v);
          this.triggerStateUpdate();
          this.onChanged();
        }
      });
    }
    DEBUG && console.log("InteractiveEditor children", children);
    if (children.length === 0) {
      return /*#__PURE__*/React.createElement(BucketComponent, {
        className: "ib-empty-bucket"
      }, "Drop a Component Here");
    }
    return children.map((child, key) => {
      var _child$props;
      if (child === undefined) {
        return /*#__PURE__*/React.createElement("div", {
          className: "empty-component",
          "data-editor-component": true,
          style: {
            minHeight: '32px'
          }
        });
      }
      let onClick = e => {
        e.stopPropagation();
        if (e.shiftKey) {
          let c = this.state.selectedChildren.slice();
          let i = c.indexOf(children[key]);
          if (i === -1) {
            c.push(children[key]);
          } else {
            c.splice(i, 1);
          }
          this.setState({
            selectedChildren: c,
            selectedChild: c.length === 1 ? c[0] : undefined,
            selectedParent: children,
            selectedKey: c.length === 1 ? key : undefined
          });
          this.closeInspector();
        } else {
          var _children$key;
          if (typeof children[key] === "string" || text_tags.includes((_children$key = children[key]) === null || _children$key === void 0 ? void 0 : _children$key.tagName)) {
            this.closeInspector();
            if (this.state.selectedChild === children[key]) {
              this.setState({
                textMode: true
              });
            } else {
              this.setState({
                selectedChild: children[key],
                selectedChildren: [children[key]],
                selectedParent: children,
                selectedKey: key
              });
            }
          } else {
            if (this.state.selectedChild === children[key]) {
              this.openInspector(children, key);
            } else {
              this.closeInspector();
              this.setState({
                selectedChild: children[key],
                selectedChildren: [children[key]],
                selectedParent: children,
                selectedKey: key
              });
            }
          }
        }
        DEBUG && console.log("onClickCapture!", child);
      };
      if (child.type === "Interactive") {
        return /*#__PURE__*/React.createElement(ErrorBoundary, {
          key: key
        }, /*#__PURE__*/React.createElement(Navigation, {
          onReset: () => {
            DEBUG && console.log("navigation onReset InteractiveEditor");
            clearStorage();
            this.changePage(0);
          }
        }, this.renderEditorComponents(child, "panels")));
      } else if (child.type === "Panel") {
        return /*#__PURE__*/React.createElement(ErrorBoundary, {
          key: key
        }, /*#__PURE__*/React.createElement(Page, null, this.renderEditorComponents(child, "children")));
      }
      return /*#__PURE__*/React.createElement(Sortable, {
        key: `${path.join("-")}-${key}-${uniqueId(child)}`,
        id: `${path.join("-")}-${key}-${uniqueId(child)}`,
        index: key,
        onMove: (fromIndex, toIndex, fromHeight) => {
          let children = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
          if (children[fromIndex] === undefined) {
            children[fromIndex] = new Element({
              tagName: 'div',
              props: {
                className: 'dragged-in-placeholder',
                style: {
                  minHeight: fromHeight
                }
              }
            });
          }
          if (fromIndex !== toIndex) {
            const temp = children[fromIndex];
            children.splice(fromIndex, 1);
            children.splice(toIndex, 0, temp);
            this.forceUpdate();
          }
        },
        type: "Component",
        "data-editor-component": true,
        "data-editor-component-selected": this.state.selectedChildren.includes(children[key]) ? true : undefined,
        "data-placeholder": child !== null && child !== void 0 && (_child$props = child.props) !== null && _child$props !== void 0 && _child$props.className ? child.props.className.includes('dragged-in-placeholder') : undefined,
        onMouseDownCapture: onClick
      }, /*#__PURE__*/React.createElement(InteractiveRenderer, {
        data: child,
        storageKey: `children-${uniqueId(child)}-${key}`
      }));
    });
  }
  // Render the editor.
  render() {
    DEBUG && console.log("InteractiveEditor render");
    this.renderChildren_key = 0;
    return /*#__PURE__*/React.createElement("div", {
      onMouseDown: e => {
        e.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(Breadcrumb, null, this.state.breadCrumb.slice(0, this.state.breadCrumbLength).map((x, i) => {
      const active = i === this.state.breadCrumbLength - 1;
      let item = pathReduce(x.parent(), x.path);
      if (x.parent === undefined || item === undefined) {
        return undefined;
      }
      if (!item.props && !item.children && item.type !== "Content") {
        return undefined;
      }
      return /*#__PURE__*/React.createElement(Breadcrumb.Item, {
        key: i,
        href: active ? undefined : "#",
        active: active,
        onClick: e => {
          e.preventDefault();
          DEBUG && console.log("BREADCRUMB CLICK", i, x);
          if (x.onClick) {
            x.onClick(i);
          } else {
            this.breadcrumbChop(i + 1);
            this.setState({
              scopedDataParent: x.parent(),
              scopedDataPath: x.path
            });
          }
        }
      }, x.name);
    })), this.state.scopedDataParent && this.renderEditorComponents(this.state.scopedDataParent, this.state.scopedDataPath), this.state.scopedDataParent && /*#__PURE__*/React.createElement(ButtonToolbar, null, /*#__PURE__*/React.createElement(Button, {
      variant: "success",
      title: "Add selected component",
      className: "mr-2 mt-2",
      disabled: this.props.selectedComponent === undefined,
      onClick: () => {
        let arr = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
        let cloned = JSON.parse(JSON.stringify(ComponentToolbox[this.props.selectedComponent]));
        let hydrated = deepMap(cloned, hydrate);
        let i = arr.indexOf(this.state.selectedChild);
        if (i === -1) {
          i = arr.length;
        } else {
          i++;
        }
        DEBUG && console.log("Added new component", i, hydrated);
        arr.splice(i, 0, hydrated);
        this.triggerStateUpdate();
        this.onChanged();
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-plus-lg"
    })), /*#__PURE__*/React.createElement(Button, {
      variant: "info",
      title: "Open item properties",
      className: "mr-2 mt-2",
      disabled: this.state.selectedChild === undefined,
      onClick: () => {
        DEBUG && console.log("... clicked", this.state.scopedDataParent);
        this.openInspector(this.state.selectedParent, this.state.selectedKey);
      }
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-three-dots"
    })), /*#__PURE__*/React.createElement(CopyComponentButton, {
      selectedChildren: this.state.selectedChildren,
      selectedChild: this.state.selectedChild,
      setCopiedComponent: this.setCopiedComponent,
      onChanged: this.onChanged
    }), /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      title: "Paste Component",
      className: "mr-2 mt-2",
      disabled: this.state.copiedComponent === null,
      onClick: () => {
        let arr = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
        let i = arr.indexOf(this.state.selectedChild);
        if (i === -1) {
          i = arr.length;
        } else {
          i++;
        }
        arr.splice(i, 0, this.state.copiedComponent);
        this.triggerStateUpdate();
        this.onChanged();
      }
    }, /*#__PURE__*/React.createElement("i", {
      class: "bi bi-clipboard2-plus"
    })), /*#__PURE__*/React.createElement(DestructiveButton, {
      variant: "danger",
      title: "Delete selected item(s)",
      className: "mr-2 mt-2",
      confirmation: /*#__PURE__*/React.createElement("i", {
        className: "bi bi-question-lg"
      }),
      disabled: this.state.selectedChildren.length === 0,
      text: /*#__PURE__*/React.createElement("i", {
        className: "bi bi-trash"
      }),
      onClick: () => {
        let arr = pathReduce(this.state.scopedDataParent, this.state.scopedDataPath);
        for (const del of this.state.selectedChildren) {
          let i = arr.indexOf(del);
          if (i !== -1) {
            arr.splice(i, 1);
          }
        }
        this.props.clearInspector();
        this.triggerStateUpdate();
        this.onChanged();
      }
    })), /*#__PURE__*/React.createElement("code", {
      style: {
        display: 'none'
      }
    }, JSON.stringify(pathReduce(this.state.scopedDataParent, this.state.scopedDataPath))));
  }
}